/**
 * Client model snapshot — supply §5.2 / §10 (ISSUE-221, ISSUE-222, ISSUE-220, ISSUE-225).
 * Routes: /client/:companyId/snapshot | priorities | history
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Alert, Card, Space, Spin, Table, Tabs, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import { useIntentmlSnapshotInvalidation } from '../../hooks/useIntentmlSnapshotInvalidation';
import {
  getReportSnapshot,
  postProjectionSimulate,
} from '../../services/api/supplySnapshotService';
import type {
  ProjectionSimulateResponse,
  ReportSnapshotEnvelope,
  ReportSnapshotKind,
} from '../../types/clientSnapshotReports';
import { ReportSnapshotEnvelopeView } from '../../components/ReportSnapshot';
import styles from './ClientModelClientPage.module.css';

const REPORT_KINDS: ReportSnapshotKind[] = ['executive', 'growth', 'success', 'finance'];

function tabFromPath(pathname: string): 'snapshot' | 'priorities' | 'history' {
  const seg = pathname.replace(/\/+$/, '').split('/').pop() || 'snapshot';
  if (seg === 'priorities' || seg === 'history') return seg;
  return 'snapshot';
}

export const ClientModelClientPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const tabKey = tabFromPath(location.pathname);

  const [loading, setLoading] = useState(false);
  const [reportsByKind, setReportsByKind] = useState<
    Partial<Record<ReportSnapshotKind, ReportSnapshotEnvelope>>
  >({});
  const [reportError, setReportError] = useState<string | null>(null);
  const [projection, setProjection] = useState<ProjectionSimulateResponse | null>(null);
  const [projectionError, setProjectionError] = useState<string | null>(null);

  const loadSnapshotData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setReportError(null);
    setProjectionError(null);
    const results = await Promise.allSettled(
      REPORT_KINDS.map((k) => getReportSnapshot(k, companyId))
    );
    const next: Partial<Record<ReportSnapshotKind, ReportSnapshotEnvelope>> = {};
    results.forEach((r, i) => {
      const kind = REPORT_KINDS[i];
      if (r.status === 'fulfilled') {
        next[kind] = r.value;
      }
    });
    if (Object.keys(next).length === 0) {
      setReportError('No reports could be loaded.');
    }
    setReportsByKind(next);
    try {
      const proj = await postProjectionSimulate({
        company_id: companyId,
        scenario: 'baseline',
        months: 6,
      });
      setProjection(proj);
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && (e.response?.status === 404 || e.response?.status === 501)) {
        setProjectionError('Projection API not available yet.');
      } else {
        setProjectionError(axios.isAxiosError(e) ? String(e.response?.data?.detail ?? e.message) : 'Projection failed');
      }
      setProjection(null);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId && tabKey === 'snapshot') {
      void loadSnapshotData();
    }
  }, [companyId, tabKey, loadSnapshotData]);

  useIntentmlSnapshotInvalidation(companyId, loadSnapshotData);

  const trajectoryColumns: ColumnsType<Record<string, unknown>> = useMemo(
    () => [
      { title: 'Month', dataIndex: 'month_index', key: 'month_index', width: 72 },
      { title: 'Entropy', dataIndex: 'entropy', key: 'entropy' },
      {
        title: 'Index proxy',
        dataIndex: 'systemic_index_proxy',
        key: 'systemic_index_proxy',
        render: (v: unknown) => (typeof v === 'number' ? v.toFixed(2) : '—'),
      },
      { title: 'Note', dataIndex: 'note', key: 'note', ellipsis: true },
    ],
    []
  );

  if (!companyId) {
    return (
      <div className={styles.wrap}>
        <Alert type="error" message="Missing company id in URL." showIcon />
      </div>
    );
  }

  const base = `/client/${companyId}`;

  return (
    <div className={styles.wrap}>
      <Typography.Title level={3} className={styles.title}>
        Client model
      </Typography.Title>
      <Tabs
        className={styles.tabs}
        activeKey={tabKey}
        onChange={(k) => navigate(`${base}/${k}`)}
        items={[
          { key: 'snapshot', label: 'Snapshot' },
          { key: 'priorities', label: 'Priorities' },
          { key: 'history', label: 'History' },
        ]}
      />

      {tabKey === 'priorities' && (
        <Card size="small" className={styles.card}>
          <Typography.Paragraph>
            Priorities derived from your company model will appear here. Connect roadmap work in a follow-on
            iteration.
          </Typography.Paragraph>
        </Card>
      )}

      {tabKey === 'history' && (
        <Card size="small" className={styles.card}>
          <Typography.Paragraph>
            Session and change history for this company will appear here.
          </Typography.Paragraph>
        </Card>
      )}

      {tabKey === 'snapshot' && (
        <Space direction="vertical" size="large" className={styles.stack}>
          <Spin spinning={loading}>
            <Alert
              type="info"
              showIcon
              className={styles.alert}
              message="Snapshot behavior"
              description="WP-010 reports load automatically for this company. The entropy trajectory below refreshes with a fixed 6-month baseline when you open this tab (or after an IntentML import). On the hierarchical product Projections dashboard, use Run simulation there to choose scenario and month range."
            />
            {reportError && (
              <Alert type="warning" message="Reports" description={reportError} showIcon className={styles.alert} />
            )}
            {REPORT_KINDS.filter((k) => reportsByKind[k]).map((k) => (
              <ReportSnapshotEnvelopeView key={k} envelope={reportsByKind[k]!} />
            ))}

            <Card title="Entropy trajectory (projection simulate)" size="small" className={styles.card}>
              {projectionError && (
                <Alert type="info" message={projectionError} showIcon className={styles.alert} />
              )}
              {projection && !projectionError && (
                <>
                  <Typography.Text type="secondary">
                    Scenario {projection.scenario}, {projection.months} months
                    {projection.stub ? ' (stub)' : ''}
                  </Typography.Text>
                  <div className={styles.tableScroll}>
                    <Table
                      size="small"
                      rowKey={(r) => String((r as { month_index?: number }).month_index)}
                      columns={trajectoryColumns}
                      dataSource={projection.trajectory as Record<string, unknown>[]}
                      pagination={false}
                    />
                  </div>
                </>
              )}
            </Card>
          </Spin>
        </Space>
      )}
    </div>
  );
};

export default ClientModelClientPage;
